import { useEffect, useState } from 'react'
import { FlaskConical } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui'
import { SimulationPanel } from '@/components/admin/SimulationPanel'
import { cn } from '@/lib/utils'

const SHORTCUT_HINT = 'Ctrl/⌘ + Shift + N'

/**
 * The "hidden" simulation widget.
 *
 * Invisible until summoned with Ctrl/Cmd + Shift + N (or `?sim=1` in the URL,
 * which is the handy way to reach it on a phone against the deployed build).
 * It exists so notifications can be exercised on any device without a server.
 */
export function HiddenSimulationWidget() {
  const [available, setAvailable] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('sim') === '1') {
      setAvailable(true)
    }

    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === 'n') {
        event.preventDefault()
        setAvailable(true)
        setOpen((current) => !current)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  if (!available) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'fixed bottom-4 right-4 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition hover:scale-105 no-print',
        )}
        aria-label={`Open the notification simulation panel (${SHORTCUT_HINT})`}
        title={`Notification simulator — ${SHORTCUT_HINT}`}
      >
        <FlaskConical className="h-5 w-5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Notification simulator</DialogTitle>
            <DialogDescription>
              Push a notification into the live feed to test the bell badge, the inline
              Accept/Decline flow and offline behaviour. Toggle with {SHORTCUT_HINT}.
            </DialogDescription>
          </DialogHeader>
          <SimulationPanel />
        </DialogContent>
      </Dialog>
    </>
  )
}
